// App.tsx
import React from 'react';
import { ArrowLeft, Github, ExternalLink, Database, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DProject4 from "@/assets/achivements/DProject4.png";
import cloud from "@/assets/achivements/cloud.jpg";
import image from "@/assets/achivements/image.png";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const HomelabCloudStorage: React.FC = () => {
  return (
    <main className="min-h-screen bg-background">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Homelab Cloud Storage</h1>
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">TrueNAS Scale</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Cloudflare Tunnel</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Docker</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">ZFS</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">RAID</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Linux</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Shell Scripting</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Carousel */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={DProject4} alt="Placeholder" className="w-full object-cover" />
              </div>
              <div>
                <img src={cloud} alt="Homelab Cloud Storage Dashboard" className="w-full object-cover" />
              </div>
              <div>
                <img src={image} alt="Homelab Cloud Storage Dashboard" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  A sophisticated self-hosted cloud storage solution leveraging TrueNAS Scale on repurposed enterprise hardware. This project addresses the growing need for secure, cost-effective data storage while maintaining full control over personal and professional data. Features include secure remote access via Cloudflare Tunnel, automated backups, media streaming, and containerized applications.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">How It Works</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  The system is built on TrueNAS Scale, utilizing ZFS RAID for data redundancy and Docker for containerized applications. Cloudflare Tunnel provides secure remote access, while automated backup scripts and resource monitoring ensure reliability. The platform supports media streaming and is optimized for both home and small business use.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Key Features</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-6">
                  <li>Secure remote access with Cloudflare Tunnel</li>
                  <li>Data redundancy with ZFS RAID</li>
                  <li>Automated backup system</li>
                  <li>Media streaming capabilities</li>
                  <li>Containerized applications</li>
                  <li>Resource monitoring and alerts</li>
                  <li>Cost-effective enterprise storage</li>
                </ul>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Technical Implementation</h3>
                <p className="text-muted-foreground leading-relaxed">
                  The solution uses TrueNAS Scale as the core OS, with ZFS for advanced storage management and RAID for redundancy. Docker is used for deploying additional services. Cloudflare Tunnel secures remote access, and Linux shell scripts automate backups and monitoring. The system is designed for scalability and security, with a focus on privacy and cost savings.
                </p>
              </div>
              
              {/* Project demo */}
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Demo</h2>
                <div className="aspect-video w-full overflow-hidden rounded-lg">
                  <iframe 
                    width="100%" 
                    height="400" 
                    src="https://www.youtube.com/embed/WGM9GrnDn1Q?t=96" 
                    title="Homelab Cloud Storage Demo" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="aspect-video"
                  ></iframe>
                </div>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://github.com/Mohan-Balaji/homelab-cloud" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Github className="mr-2 h-5 w-5" />
                      <span>Source Code</span>
                    </a>
                  </Button>
                  <Button variant="outline" size="lg" className="w-full justify-start" asChild>
                    <a href="https://youtu.be/WGM9GrnDn1Q?si=lO3OTS2m2JkIaTAD&t=96" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      <Play className="mr-2 h-5 w-5" />
                      <span>Video Demo</span>
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">T</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">TrueNAS Scale</p>
                      <p className="text-sm text-muted-foreground">Storage OS</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">C</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Cloudflare Tunnel</p>
                      <p className="text-sm text-muted-foreground">Remote Access</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">D</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Docker</p>
                      <p className="text-sm text-muted-foreground">Containerization</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">Z</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">ZFS RAID</p>
                      <p className="text-sm text-muted-foreground">Data Redundancy</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">L</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Linux</p>
                      <p className="text-sm text-muted-foreground">Operating System</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">S</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Shell Scripting</p>
                      <p className="text-sm text-muted-foreground">Automation</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Impact</h3>
                <p className="text-muted-foreground mb-4">
                  Successfully reduced storage costs by 70% while providing enterprise-grade features and maintaining full control over data. The solution offers a compelling alternative to traditional cloud storage services, particularly for users concerned about data privacy and long-term costs.
                </p>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Cost Savings</span>
                    <span className="text-primary font-medium">70%</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[70%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Data Privacy</span>
                    <span className="text-primary font-medium">Full Control</span>
                  </div>
                  <div className="h-2 w-full bg-muted/30 rounded-full overflow-hidden">
                    <div className="h-full w-[100%] bg-gradient-to-r from-primary to-purple-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Inventory_Management_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Inventory Management System</h3>
              <p className="text-muted-foreground line-clamp-2">A system designed to streamline stock tracking, optimize warehouse operations, and automate inventory updates using modern web technologies.</p>
            </Link>
            <Link to="/Avinyaa_2025" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Avinyaa 2025 Website</h3>
              <p className="text-muted-foreground line-clamp-2">A website for a college symposium with modern web technologies.</p>
            </Link>
            <Link to="/Smart_Irrigation_System_using_Precision_Farming" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Smart Irrigation System</h3>
              <p className="text-muted-foreground line-clamp-2">A system that optimizes water usage in agriculture by monitoring soil moisture levels.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomelabCloudStorage;
